import React, { useState } from 'react';
import { Input, Button, Select, Space, message, Spin } from 'antd';
import { ThunderboltOutlined, FileImageOutlined } from '@ant-design/icons';
import axiosHttp from '../../../appRedux/services/axiosCommon';

const { TextArea } = Input;

const PRESETS = [
  { label: 'Banner (1200×400)', width: 1200, height: 400, ratio: '3:1' },
  { label: 'Header (1920×600)', width: 1920, height: 600, ratio: '3.2:1' },
  { label: 'Square (800×800)', width: 800, height: 800, ratio: '1:1' },
];

const MODELS = [
  { label: 'FLUX.1 Schnell (Fast)', value: '@cf/black-forest-labs/flux-1-schnell' },
  { label: 'FLUX.1 Dev (Quality)', value: '@cf/black-forest-labs/flux-1-dev' },
];

const PROMPT_PRESETS = [
  {
    label: 'Vibrant Gradient',
    prompt: 'A clean abstract gradient background transitioning from bright orange to yellow. Minimalist, high resolution, soft lighting, modern design.'
  },
  {
    label: 'Dark Glassmorphism',
    prompt: 'A dark mode abstract background with deep navy blue and purple colors. Glowing neon accents, frosted glass effect, minimalist geometric shapes, tech aesthetic.'
  },
  {
    label: 'Soft Pastel',
    prompt: 'A calming pastel background featuring light pink and lavender colors. Flowing shapes, airy atmosphere, perfect for typography overlay.'
  },
  {
    label: 'Professional',
    prompt: 'A clean abstract background in shades of light grey and blue. Geometric patterns, sleek design, bright lighting, uncluttered.'
  }
];

export default function GenerateTab({ onSelectImage, activeWidth, activeHeight }) {
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [selectedModel, setSelectedModel] = useState(MODELS[0].value);

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      message.error('Please enter a description prompt first.');
      return;
    }

    setLoading(true);
    setGeneratedImage(null);

    try {
      const response = await axiosHttp.post('/v2/ai/image/generate', {
        prompt: prompt.trim(),
        width: activeWidth || 1200,
        height: activeHeight || 628,
        model: selectedModel,
      });

      if (response.data && response.data.status && response.data.results?.image) {
        // Results image can be a URL or base64 string
        let imgSource = response.data.results.image;
        
        // Handle double-encoded JSON base64 string
        if (imgSource && imgSource.startsWith('eyJyZXN1bHQi')) {
          try {
            const decodedStr = window.atob(imgSource);
            const parsed = JSON.parse(decodedStr);
            if (parsed && parsed.result && parsed.result.image) {
              imgSource = parsed.result.image;
            }
          } catch (e) {
            console.error("Failed to decode double-encoded image string:", e);
          }
        }

        const formattedSource = (imgSource.startsWith('data:') || imgSource.startsWith('http://') || imgSource.startsWith('https://'))
          ? imgSource 
          : `data:image/png;base64,${imgSource}`;
        
        setGeneratedImage(formattedSource);
        message.success('Background generated successfully!');
      } else {
        message.error(response.data?.message || 'Failed to generate background image.');
      }
    } catch (err) {
      console.error('Image Generation Error:', err);
      message.error('An error occurred while generating the image.');
    } finally {
      setLoading(false);
    }
  };

  const handleUseBackground = () => {
    if (generatedImage) {
      onSelectImage(generatedImage, activeWidth, activeHeight);
    }
  };

  return (
    <div className="generate-tab-container">
      {/* Left Input Configuration Panel */}
      <div className="generate-left-panel">
        <div>
          <label style={{ fontWeight: 600, fontSize: '13px', color: '#434343', display: 'block', marginBottom: '8px' }}>
            Prompt / Background Description
          </label>
          <TextArea
            rows={4}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="e.g. A futuristic learning desk with abstract neon grids, clean composition, studio lighting, high resolution background, copy space..."
            className="prompt-textarea"
            disabled={loading}
          />
          <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {PROMPT_PRESETS.map((preset) => (
              <Button
                key={preset.label}
                size="small"
                onClick={() => setPrompt(preset.prompt)}
                disabled={loading}
                style={{ fontSize: '11px', borderRadius: '4px' }}
              >
                {preset.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Dimension viewer (Read-only status displaying the active canvas size) */}
        <div>
          <label style={{ fontWeight: 600, fontSize: '13px', color: '#434343', display: 'block', marginBottom: '8px' }}>
            Active Canvas Size
          </label>
          <div style={{ background: '#f5f5f5', border: '1px solid #d9d9d9', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', fontWeight: 500, color: '#262626', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FileImageOutlined style={{ color: '#1890ff', fontSize: '16px' }} />
            <span>{activeWidth || 1200} × {activeHeight || 628}</span>
          </div>
        </div>

        {/* Model selector */}
        <div>
          <label style={{ fontWeight: 600, fontSize: '13px', color: '#434343', display: 'block', marginBottom: '8px' }}>
            AI Model
          </label>
          <Select
            value={selectedModel}
            onChange={setSelectedModel}
            options={MODELS}
            style={{ width: '100%' }}
            disabled={loading}
          />
        </div>

        {/* Action Button */}
        <Button
          type="primary"
          icon={<ThunderboltOutlined />}
          onClick={handleGenerate}
          loading={loading}
          style={{
            height: '42px',
            borderRadius: '8px',
            background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
            border: 'none',
            fontWeight: 500,
            marginTop: '12px',
          }}
        >
          Generate Background
        </Button>
      </div>

      {/* Right Preview Panel */}
      <div className="generate-right-panel">
        {loading && (
          <div className="loading-overlay">
            <Spin size="large" />
            <span style={{ fontWeight: 500, color: '#1890ff', fontSize: '14px' }}>
              AI is painting your background...
            </span>
          </div>
        )}

        {generatedImage ? (
          <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', padding: '16px' }}>
            <div style={{ flex: 1, overflow: 'hidden', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#eaeaea' }}>
              <img
                src={generatedImage}
                alt="AI Generated Background"
                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
              />
            </div>
            <Button
              type="primary"
              icon={<FileImageOutlined />}
              onClick={handleUseBackground}
              style={{
                marginTop: '12px',
                height: '38px',
                borderRadius: '8px',
                background: '#52c41a',
                borderColor: '#52c41a',
                fontWeight: 600,
              }}
            >
              Use as Background in Editor
            </Button>
          </div>
        ) : (
          !loading && (
            <div style={{ textAlign: 'center', color: '#bfbfbf' }}>
              <FileImageOutlined style={{ fontSize: '48px', marginBottom: '12px' }} />
              <p style={{ margin: 0, fontSize: '13px' }}>Your AI generated background will display here</p>
              <p style={{ margin: 0, fontSize: '11px', color: '#d9d9d9' }}>Input a prompt on the left to start</p>
            </div>
          )
        )}
      </div>
    </div>
  );
}
